import { useEffect, useState } from 'react';
import { FaChevronLeft } from 'react-icons/fa';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { PageSpinner } from '../../../components/spinner/Spinner';
import { config } from '../../../Constants';
import toast from 'react-hot-toast';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

const Investment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingProfit, setAddingProfit] = useState(false);
  const [removingProfit, setRemovingProfit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addProfitAmount, setAddProfitAmount] = useState('');
  const [sendProfitEmail, setSendProfitEmail] = useState(true);
  const [removeProfitAmount, setRemoveProfitAmount] = useState('');
  const [cancelModalOpened, { open: openCancelModal, close: closeCancelModal }] =
    useDisclosure(false);
  const [completeModalOpened, { open: openCompleteModal, close: closeCompleteModal }] =
    useDisclosure(false);
  const { token } = JSON.parse(secureLocalStorage.getItem('user-info'));
  const baseURL = config.baseURL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/investments/admin/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(data);
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load investment');
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleAddProfit = async (e) => {
    e.preventDefault();
    const amount = Number(addProfitAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setAddingProfit(true);
    try {
      const { data: updated } = await axios.patch(
        `${baseURL}/investments/admin/${id}`,
        { addProfit: amount, sendEmail: sendProfitEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(updated.investment);
      setAddProfitAmount('');
      const emailMsg = sendProfitEmail ? ' and email sent' : '';
      toast.success(`$${amount} profit added successfully${emailMsg}`);
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to add profit');
    } finally {
      setAddingProfit(false);
    }
  };

  const handleRemoveProfit = async (e) => {
    e.preventDefault();
    const amount = Number(removeProfitAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount > data.profitAccrued) {
      toast.error(`Cannot remove more than current profit ($${data.profitAccrued.toFixed(2)})`);
      return;
    }

    setRemovingProfit(true);
    try {
      const { data: updated } = await axios.patch(
        `${baseURL}/investments/admin/${id}`,
        { removeProfit: amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(updated.investment);
      setRemoveProfitAmount('');
      toast.success(`$${amount} profit removed successfully`);
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to remove profit');
    } finally {
      setRemovingProfit(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await axios.patch(
        `${baseURL}/investments/admin/${id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Investment cancelled and refunded');
      navigate('/dashboard/investments');
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to cancel investment');
      setSubmitting(false);
      closeCancelModal();
    }
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      const { data: updated } = await axios.patch(
        `${baseURL}/investments/admin/${id}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(updated.investment);
      toast.success('Investment marked as completed');
      closeCompleteModal();
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to complete investment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className='content investment-page'>
        <PageSpinner />
      </main>
    );
  }

  if (!data) {
    return (
      <main className='content investment-page'>
        <div className='title-btn'>
          <Link to='/dashboard/investments'>
            <FaChevronLeft />
          </Link>
          <h1 className='title'>Investment Not Found</h1>
        </div>
      </main>
    );
  }

  const progress = Math.min(
    100,
    ((data.profitAccrued / data.totalExpectedProfit) * 100).toFixed(1)
  );

  return (
    <main className='content investment-page'>
      <div className='title-btn'>
        <Link to='/dashboard/investments'>
          <FaChevronLeft />
        </Link>
        <h1 className='title'>Investment Details</h1>
      </div>

      <div className='blocks-cont'>
        <div className='brief-block'>
          <h2>{data.planTitle}</h2>
          <span className={`status ${data.status}`}>{data.status}</span>
          <h3>${data.amount.toLocaleString()}</h3>
          <p>{data.userName}</p>
          <p className='email'>{data.email}</p>
        </div>

        <div className='grid'>
          <div className='block'>
            <div className='title-block'>
              <h2 className='title'>Investment Info</h2>
            </div>
            <hr />
            <div className='info-grid'>
              <div className='info-item'>
                <span>Amount Invested</span>
                <strong>${data.amount.toLocaleString()}</strong>
              </div>
              <div className='info-item'>
                <span>Expected Profit</span>
                <strong>${data.totalExpectedProfit.toLocaleString()}</strong>
              </div>
              <div className='info-item'>
                <span>Profit Accrued</span>
                <strong className='profit'>${data.profitAccrued.toFixed(2)}</strong>
              </div>
              <div className='info-item'>
                <span>Daily Profit Rate</span>
                <strong>${data.dailyProfitRate.toFixed(2)}/day</strong>
              </div>
              <div className='info-item'>
                <span>Duration</span>
                <strong>{data.duration} days</strong>
              </div>
              <div className='info-item'>
                <span>Expected Profit %</span>
                <strong>{data.expectedProfit}%</strong>
              </div>
              <div className='info-item'>
                <span>Start Date</span>
                <strong>{formatDate(data.startDate)}</strong>
              </div>
              <div className='info-item'>
                <span>End Date</span>
                <strong>{formatDate(data.endDate)}</strong>
              </div>
            </div>

            <div className='progress-section'>
              <div className='progress-header'>
                <span>Profit Progress</span>
                <span>{progress}%</span>
              </div>
              <div className='progress-bar'>
                <div className='progress-fill' style={{ width: `${progress}%` }} />
              </div>
              <p className='progress-info'>
                ${data.profitAccrued.toFixed(2)} of ${data.totalExpectedProfit.toLocaleString()}
              </p>
            </div>
          </div>

          {data.status === 'active' && (
            <div className='block'>
              <div className='title-block'>
                <h2 className='title'>Manage Profit</h2>
                <h4>Add or remove profit from this investment</h4>
              </div>
              <hr />
              <div className='profit-forms'>
                <form onSubmit={handleAddProfit} className='profit-form'>
                  <div className='form-control'>
                    <label>Add Profit ($)</label>
                    <input
                      type='number'
                      value={addProfitAmount}
                      onChange={(e) => setAddProfitAmount(e.target.value)}
                      placeholder='Amount to add'
                      min='0'
                      step='0.01'
                    />
                  </div>
                  <label className='checkbox-control'>
                    <input
                      type='checkbox'
                      checked={sendProfitEmail}
                      onChange={(e) => setSendProfitEmail(e.target.checked)}
                    />
                    <span>Send profit email to user</span>
                  </label>
                  <button type='submit' className='btn-primary' disabled={addingProfit}>
                    {addingProfit ? 'Adding...' : 'Add Profit'}
                  </button>
                </form>

                <form onSubmit={handleRemoveProfit} className='profit-form'>
                  <div className='form-control'>
                    <label>Remove Profit ($)</label>
                    <input
                      type='number'
                      value={removeProfitAmount}
                      onChange={(e) => setRemoveProfitAmount(e.target.value)}
                      placeholder='Amount to remove'
                      min='0'
                      max={data.profitAccrued}
                      step='0.01'
                    />
                  </div>
                  <button type='submit' className='btn-danger' disabled={removingProfit || data.profitAccrued <= 0}>
                    {removingProfit ? 'Removing...' : 'Remove Profit'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {data.status === 'active' && (
            <div className='block'>
              <div className='title-block'>
                <h2 className='title'>Actions</h2>
                <h4>Manage this investment</h4>
              </div>
              <hr />
              <div className='actions-section'>
                <button className='btn-success' onClick={openCompleteModal}>
                  Mark as Completed
                </button>
                <button className='btn-danger' onClick={openCancelModal}>
                  Cancel & Refund
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal
        opened={cancelModalOpened}
        onClose={closeCancelModal}
        title='Cancel Investment'
        centered
        className='confirm-modal'
      >
        <div className='confirm-content'>
          <p>
            Are you sure you want to cancel this investment for{' '}
            <strong>{data.userName}</strong>?
          </p>
          <p className='info-text'>
            The user will be refunded ${data.amount.toLocaleString()} to their deposit
            balance.
          </p>
          <div className='confirm-buttons'>
            <button className='btn-cancel' onClick={closeCancelModal} disabled={submitting}>
              Keep Investment
            </button>
            <button
              className='btn-confirm btn-danger'
              onClick={handleCancel}
              disabled={submitting}
            >
              {submitting ? 'Cancelling...' : 'Cancel & Refund'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Complete Modal */}
      <Modal
        opened={completeModalOpened}
        onClose={closeCompleteModal}
        title='Complete Investment'
        centered
        className='confirm-modal'
      >
        <div className='confirm-content'>
          <p>
            Mark this investment as <strong>completed</strong>?
          </p>
          <p className='info-text'>
            Current profit accrued: ${data.profitAccrued.toFixed(2)} of $
            {data.totalExpectedProfit.toLocaleString()}
          </p>
          <div className='confirm-buttons'>
            <button
              className='btn-cancel'
              onClick={closeCompleteModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className='btn-confirm btn-success'
              onClick={handleComplete}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : 'Mark Complete'}
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default Investment;
